import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Cloud,
  File as FileIcon,
  FileAudio,
  FileImage,
  FileVideo,
  Folder,
  HardDrive,
  Import,
  Link2,
  Unlink,
  RefreshCcw,
  Search,
  Settings as SettingsIcon,
} from 'lucide-react';
import { MAX_SOURCE_IMPORT, importableFile, normalizeSourcePath, sourceBaseName, uuidv7, type SourceEntry } from '@vidmakr/shared';
import { Badge, Button, ConfirmModal, EmptyState, Input, Modal, PageHeader, Select, Spinner, cx } from '@/components/ui';
import { useOrg } from '@/lib/hooks';
import { useSpaces } from '@/lib/spaces';
import { reducers } from '@/lib/stdb';
import { attempt, errorMessage, toast } from '@/lib/toast';
import { formatBytes } from '@/lib/format';
import { listSource, previewUrls, sourceWhere, useStorageSources, workerConfig, type SourceRow } from '@/lib/sources';

type KindFilter = 'media' | 'all' | 'video' | 'audio' | 'image';

interface Folder {
  entries: SourceEntry[];
  cursor?: string;
  error?: string;
  loading: boolean;
}

const fkey = (sourceId: string, path: string) => `${sourceId}:${path}`;
const fmtDate = (ms: number) => new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/**
 * Asset library → Sources: the organization's storage connections (buckets,
 * NAS shares on its workers) as a folder tree and a file browser. Files are
 * imported into a library (copied by a worker, never through the browser) or
 * linked in place: from a NAS share the originals then never leave it (the
 * sharing worker uploads only previews, and exports run on it).
 * The connection and folder are in the URL (`?source=<id>&path=<folder>`).
 */
export function SourcesPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { org, can } = useOrg(orgId);
  const sp = useSpaces(orgId);
  const { sources, ready } = useStorageSources(orgId);
  const [workers] = useTable(tables.orgWorkers);
  const [workerChoices] = useTable(tables.orgWorkerChoices);
  const [projects] = useTable(tables.project.where((p) => p.orgId.eq(orgId ?? '')));
  const [assets] = useTable(tables.asset.where((a) => a.orgId.eq(orgId ?? '')));
  const [params, setParams] = useSearchParams();
  const sourceId = params.get('source') ?? '';
  const path = params.get('path') ?? '';
  const source = sources.find((s) => s.id === sourceId) ?? null;
  const go = (id: string, p = '') => {
    const next = new URLSearchParams();
    if (id) next.set('source', id);
    if (p) next.set('path', p);
    setParams(next);
    setSelected(new Set());
  };

  const [folders, setFolders] = useState<Map<string, Folder>>(new Map());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<KindFilter>('media');
  const [dest, setDest] = useState('library');
  const [busy, setBusy] = useState(false);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<SourceEntry | null>(null);

  const foldersRef = useRef(folders);
  foldersRef.current = folders;
  const load = useCallback(async (src: SourceRow, p: string, more = false) => {
    const k = fkey(src.id, p);
    const cursor = more ? foldersRef.current.get(k)?.cursor : undefined;
    setFolders((m) => {
      const prev = m.get(k);
      return new Map(m).set(k, { entries: prev?.entries ?? [], cursor: prev?.cursor, loading: true });
    });
    try {
      const res = await listSource(src.id, p, cursor);
      setFolders((m) => {
        const prev = m.get(k);
        const entries = more ? [...(prev?.entries ?? []), ...res.entries] : res.entries;
        return new Map(m).set(k, { entries, cursor: res.cursor, loading: false });
      });
    } catch (e) {
      setFolders((m) => new Map(m).set(k, { entries: [], loading: false, error: errorMessage(e) }));
    }
  }, []);

  // Open the first connection; load the folder shown.
  useEffect(() => {
    if (ready && !sourceId && sources[0]) go(sources[0].id);
  }, [ready, sourceId, sources]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!source) return;
    if (!folders.has(fkey(source.id, path))) void load(source, path);
    // The tree opens down to the folder shown.
    const parts = path ? path.split('/') : [];
    const open = [fkey(source.id, ''), ...parts.slice(0, -1).map((_, i) => fkey(source.id, parts.slice(0, i + 1).join('/')))];
    for (const k of open.slice(1)) {
      const p = k.slice(source.id.length + 1);
      if (!folders.has(k)) void load(source, p);
    }
    setExpanded((e) => (open.every((k) => e.has(k)) ? e : new Set([...e, ...open])));
  }, [source?.id, path]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = source ? folders.get(fkey(source.id, path)) : undefined;
  const entries = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (current?.entries ?? [])
      .filter((e) => !q || e.name.toLowerCase().includes(q))
      .filter((e) => {
        if (e.dir || kind === 'all') return true;
        const f = importableFile(e.name);
        return kind === 'media' ? !!f : f?.kind === kind;
      })
      .sort((a, b) => (a.dir !== b.dir ? (a.dir ? -1 : 1) : a.name.localeCompare(b.name, undefined, { numeric: true })));
  }, [current, query, kind]);
  const files = entries.filter((e) => !e.dir);
  // Files of this connection already linked into vidmakr (a file is linked once per organization).
  const linkedPaths = useMemo(() => {
    const out = new Map<string, string>();
    if (!source) return out;
    const prefixes = [`nas/${source.id}/`, `src/${source.id}/`];
    for (const a of assets) {
      if (a.sourceId !== source.id) continue;
      const pre = prefixes.find((p) => a.storageKey.startsWith(p));
      if (pre) out.set(a.storageKey.slice(pre.length), a.id);
    }
    return out;
  }, [assets, source]);
  const isLinked = (e: SourceEntry) => linkedPaths.has(normalizeSourcePath(e.path) ?? e.path);
  const chosen = files.filter((e) => selected.has(e.path));
  const importable = chosen.filter((e) => importableFile(e.name));
  const linkable = importable.filter((e) => ['video', 'audio', 'image'].includes(importableFile(e.name)!.kind) && !isLinked(e));
  const linkedChosen = importable.filter(isLinked);
  const alreadyLinked = linkedChosen.length;
  const [unlinking, setUnlinking] = useState(false);
  const unlink = async () => {
    const ids = linkedChosen.map((e) => linkedPaths.get(normalizeSourcePath(e.path) ?? e.path)).filter((id): id is string => !!id);
    const ok = await attempt(async () => {
      for (const id of ids) await reducers().deleteAsset({ id, force: true });
    }, ids.length === 1 ? 'Link removed' : `Removed ${ids.length} links`);
    if (ok) setSelected(new Set());
  };

  // Thumbnails for bucket images and videos in view.
  useEffect(() => {
    if (!source || source.kind !== 's3') return;
    const want = files.filter((e) => ['image', 'video'].includes(importableFile(e.name)?.kind ?? '') && !previews[`${source.id}:${e.path}`]).slice(0, 60);
    if (!want.length) return;
    let live = true;
    previewUrls(source.id, want.map((e) => e.path))
      .then((urls) => live && setPreviews((p) => ({ ...p, ...Object.fromEntries(Object.entries(urls).map(([k, v]) => [`${source.id}:${k}`, v])) })))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [source?.id, current]); // eslint-disable-line react-hooks/exhaustive-deps

  const clickFile = (e: SourceEntry, ev: React.MouseEvent) => {
    setSelected((s) => {
      const next = new Set(ev.metaKey || ev.ctrlKey || ev.shiftKey ? s : []);
      if (ev.shiftKey && anchor) {
        const i = files.findIndex((f) => f.path === anchor);
        const j = files.findIndex((f) => f.path === e.path);
        if (i >= 0 && j >= 0) for (const f of files.slice(Math.min(i, j), Math.max(i, j) + 1)) next.add(f.path);
      } else if (next.has(e.path) && (ev.metaKey || ev.ctrlKey)) next.delete(e.path);
      else next.add(e.path);
      return next;
    });
    if (!ev.shiftKey) setAnchor(e.path);
  };
  const toggle = (e: SourceEntry) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(e.path)) next.delete(e.path);
      else next.add(e.path);
      return next;
    });

  const destination = () => {
    if (dest.startsWith('space:')) return { spaceId: dest.slice(6), projectId: '', folderId: '' };
    if (dest.startsWith('project:')) return { spaceId: '', projectId: dest.slice(8), folderId: '' };
    return { spaceId: '', projectId: '', folderId: '' };
  };
  const bring = async (mode: 'copy' | 'link') => {
    if (!source) return;
    const list = mode === 'link' ? linkable : importable;
    if (!list.length) return;
    if (list.length > MAX_SOURCE_IMPORT) return toast.error(`Choose at most ${MAX_SOURCE_IMPORT} files at a time`);
    setBusy(true);
    try {
      await reducers().importFromSource({
        sourceId: source.id,
        items: list.map((e) => ({ id: uuidv7(), path: e.path, sizeBytes: e.size })),
        mode,
        ...destination(),
      });
      toast.success(`${mode === 'link' ? 'Linking' : 'Importing'} ${list.length} file${list.length === 1 ? '' : 's'}: they appear in the asset library and process in the background`);
      setSelected(new Set());
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // Admins see every worker; editors the enabled ones by name.
  const workerName = (id: string) => workers.find((w) => w.id === id)?.name ?? workerChoices.find((w) => w.id === id)?.name;
  const sharingWorker = source?.kind === 'worker' ? workerName(workerConfig(source)?.workerId ?? '') : undefined;
  const crumbs = path ? path.split('/') : [];
  const canEdit = sp.role !== 'viewer';

  if (!org) return null;
  return (
    <div className="pb-20">
      <PageHeader
        title="Sources"
        subtitle="Company storage your team can browse and import from"
        actions={
          <>
            <Button variant="ghost" onClick={() => navigate(`/orgs/${org.id}/assets`)}>
              <ArrowLeft size={15} /> Asset library
            </Button>
            {can('admin') ? (
              <Button onClick={() => navigate(`/orgs/${org.id}/settings`)}>
                <SettingsIcon size={15} /> Manage connections
              </Button>
            ) : null}
          </>
        }
      />
      {ready && sources.length === 0 ? (
        <EmptyState
          icon={<HardDrive size={32} />}
          title="No storage connected"
          action={can('admin') ? <Button onClick={() => navigate(`/orgs/${org.id}/settings`)}>Add a connection</Button> : undefined}
        >
          {can('admin')
            ? 'Connect an S3-compatible bucket, or a NAS share mounted on one of your workers, under Settings → Storage connections.'
            : 'An admin of your organization can connect company storage (a bucket or a NAS) for the team.'}
        </EmptyState>
      ) : (
        <div className="flex items-start gap-5">
          <aside className="hidden w-64 shrink-0 rounded-xl border border-line bg-panel p-2 md:block" data-sources-tree>
            {sources.map((s) => (
              <TreeNode
                key={s.id}
                source={s}
                path=""
                label={s.name}
                depth={0}
                icon={s.kind === 's3' ? <Cloud size={14} /> : <HardDrive size={14} />}
                folders={folders}
                expanded={expanded}
                activeSource={sourceId}
                activePath={path}
                onToggle={(k, open) => {
                  setExpanded((e) => {
                    const next = new Set(e);
                    if (open) next.add(k);
                    else next.delete(k);
                    return next;
                  });
                }}
                onOpen={(p) => go(s.id, p)}
                load={(p) => !folders.has(fkey(s.id, p)) && void load(s, p)}
              />
            ))}
          </aside>
          <div className="min-w-0 flex-1">
            <div className="mb-3 md:hidden">
              <Select className="w-full" value={sourceId} onChange={(e) => go(e.target.value)}>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>
            {source ? (
              <>
                <div className="mb-3 flex flex-wrap items-center gap-3">
                  <div className="relative w-full sm:w-60">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
                    <Input className="pl-9" placeholder="Filter this folder" value={query} onChange={(e) => setQuery(e.target.value)} />
                  </div>
                  <Select value={kind} onChange={(e) => setKind(e.target.value as KindFilter)} aria-label="Type">
                    <option value="media">Media files</option>
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                    <option value="image">Images</option>
                    <option value="all">All files</option>
                  </Select>
                  <span className="truncate text-xs text-faint">{sourceWhere(source, workerName)}</span>
                  <Button className="ml-auto" size="sm" variant="ghost" onClick={() => void load(source, path)} title="Refresh">
                    <RefreshCcw size={13} /> Refresh
                  </Button>
                </div>
                <div className="mb-3 flex flex-wrap items-center gap-1 text-sm" data-source-crumbs>
                  <button className={cx('rounded px-1.5 py-0.5 hover:bg-panel-3', path ? 'text-muted' : 'text-fg')} onClick={() => go(source.id)}>
                    {source.name}
                  </button>
                  {crumbs.map((c, i) => (
                    <span key={i} className="flex items-center gap-1">
                      <ChevronRight size={14} className="text-faint" />
                      <button
                        className={cx('rounded px-1.5 py-0.5 hover:bg-panel-3', i === crumbs.length - 1 ? 'text-fg' : 'text-muted')}
                        onClick={() => go(source.id, crumbs.slice(0, i + 1).join('/'))}
                      >
                        {c}
                      </button>
                    </span>
                  ))}
                </div>
                {current?.error ? (
                  <div className="rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-danger" data-source-error>
                    {current.error}
                  </div>
                ) : !current || (current.loading && !current.entries.length) ? (
                  <div className="flex justify-center py-16 text-muted">
                    <Spinner />
                  </div>
                ) : entries.length === 0 ? (
                  <EmptyState icon={<Folder size={28} />} title={query ? 'Nothing matches' : 'This folder is empty'}>
                    {kind !== 'all' && current.entries.length ? 'Other files are hidden: choose "All files" to see them.' : null}
                  </EmptyState>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-line bg-panel">
                    <table className="w-full table-fixed text-sm" data-source-table>
                      <thead className="border-b border-line text-left text-xs text-faint">
                        <tr>
                          <th className="w-10 px-3 py-2">
                            <input
                              type="checkbox"
                              aria-label="Select all"
                              checked={files.length > 0 && chosen.length === files.length}
                              onChange={(e) => setSelected(new Set(e.target.checked ? files.map((f) => f.path) : []))}
                            />
                          </th>
                          <th className="px-3 py-2 font-medium">Name</th>
                          <th className="hidden w-24 px-3 py-2 font-medium sm:table-cell">Type</th>
                          <th className="w-24 px-3 py-2 text-right font-medium">Size</th>
                          <th className="hidden w-44 px-3 py-2 font-medium md:table-cell">Modified</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((e) => {
                          const f = e.dir ? null : importableFile(e.name);
                          const url = previews[`${source.id}:${e.path}`];
                          const on = selected.has(e.path);
                          return (
                            <tr
                              key={e.path}
                              data-source-entry={e.path}
                              className={cx('cursor-pointer border-b border-line/60 last:border-0', on ? 'bg-accent/10' : 'hover:bg-panel-2')}
                              onClick={(ev) => (e.dir ? go(source.id, e.path) : clickFile(e, ev))}
                              onDoubleClick={() => !e.dir && f && setPreview(e)}
                            >
                              <td className="px-3 py-1.5" onClick={(ev) => ev.stopPropagation()}>
                                {e.dir ? null : <input type="checkbox" aria-label={`Select ${e.name}`} checked={on} onChange={() => toggle(e)} />}
                              </td>
                              <td className="max-w-0 px-3 py-1.5">
                                <div className="flex items-center gap-3">
                                  <Thumb entry={e} url={url} />
                                  <span className="truncate font-medium" title={e.path}>
                                    {e.name}
                                  </span>
                                  {!e.dir && isLinked(e) ? (
                                    <Badge tone="accent">
                                      <Link2 size={10} /> Linked
                                    </Badge>
                                  ) : null}
                                </div>
                              </td>
                              <td className="hidden px-3 py-1.5 text-muted sm:table-cell">{e.dir ? 'Folder' : f ? f.kind[0].toUpperCase() + f.kind.slice(1) : 'Other'}</td>
                              <td className="px-3 py-1.5 text-right text-muted">{e.dir ? '' : formatBytes(e.size)}</td>
                              <td className="hidden whitespace-nowrap px-3 py-1.5 text-muted md:table-cell">{e.modifiedAt ? fmtDate(e.modifiedAt) : ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {current.cursor ? (
                      <div className="border-t border-line p-2 text-center">
                        <Button size="sm" variant="ghost" loading={current.loading} onClick={() => void load(source, path, true)}>
                          Load more
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}

      {chosen.length > 0 && source ? (
        <div className="fixed bottom-4 left-1/2 z-30 flex w-[min(720px,calc(100vw-2rem))] -translate-x-1/2 flex-wrap items-center gap-3 rounded-2xl border border-line bg-panel p-3 shadow-2xl" data-source-actions>
          <div className="text-sm">
            <b>{chosen.length}</b> selected · {formatBytes(chosen.reduce((s, e) => s + e.size, 0))}
            {importable.length < chosen.length ? <span className="text-faint"> ({chosen.length - importable.length} not media)</span> : null}
            {alreadyLinked ? <span className="text-faint"> ({alreadyLinked} already linked)</span> : null}
          </div>
          {canEdit ? (
            <>
              <Select className="ml-auto" value={dest} onChange={(e) => setDest(e.target.value)} aria-label="Import into" data-source-dest>
                {sp.role !== 'guest' ? <option value="library">Shared library</option> : null}
                {sp.spaces.map((s) => (
                  <option key={s.id} value={`space:${s.id}`}>
                    Space: {s.name}
                  </option>
                ))}
                {projects.map((p) => (
                  <option key={p.id} value={`project:${p.id}`}>
                    Project: {p.name}
                  </option>
                ))}
              </Select>
              {source.kind === 's3' ? (
                <Button disabled={!linkable.length} loading={busy} onClick={() => void bring('link')} title="Keep the originals in the bucket; only proxies are stored" data-source-link>
                  <Link2 size={15} /> Link in place
                </Button>
              ) : (
                <Button
                  disabled={!linkable.length}
                  loading={busy}
                  onClick={() => void bring('link')}
                  title={`The originals never leave the NAS: ${sharingWorker ? `"${sharingWorker}"` : 'the worker sharing it'} uploads only previews (they alone count toward storage), and exports using them run on that worker`}
                  data-source-link
                >
                  <Link2 size={15} /> Link (keep on the NAS)
                </Button>
              )}
              {alreadyLinked ? (
                <Button variant="ghost" className="text-danger" onClick={() => setUnlinking(true)} title="Remove these files' links from vidmakr; the originals stay where they are" data-source-unlink>
                  <Unlink size={15} /> Unlink {alreadyLinked}
                </Button>
              ) : null}
              <Button variant="primary" disabled={!importable.length} loading={busy} onClick={() => void bring('copy')} data-source-import>
                <Import size={15} /> Import {importable.length || ''}
              </Button>
            </>
          ) : (
            <span className="ml-auto text-xs text-faint">Viewers can browse but not import.</span>
          )}
          {canEdit && source.kind === 'worker' ? (
            <p className="w-full text-xs text-faint" data-source-link-note>
              Link keeps the originals on the NAS: {sharingWorker ? <b>{sharingWorker}</b> : 'the worker sharing this folder'} uploads only previews, the editor plays those from anywhere, and exports using
              these files run on that worker, reading the full-quality originals from the folder. Import copies them into vidmakr instead.
            </p>
          ) : null}
        </div>
      ) : null}

      <ConfirmModal
        open={unlinking}
        onClose={() => setUnlinking(false)}
        title={alreadyLinked === 1 ? 'Remove link' : `Remove ${alreadyLinked} links`}
        danger
        confirmLabel="Remove"
        message={
          <p>
            {alreadyLinked === 1 ? <b className="text-fg">{linkedChosen[0]?.name}</b> : `These ${alreadyLinked} files`} and{' '}
            {alreadyLinked === 1 ? 'its' : 'their'} previews are removed from vidmakr, and clips using {alreadyLinked === 1 ? 'it' : 'them'} are removed from timelines. The
            original{alreadyLinked === 1 ? '' : 's'} stay{alreadyLinked === 1 ? 's' : ''} here, untouched, and can be linked again.
          </p>
        }
        onConfirm={unlink}
      />

      {preview && source ? (
        <Modal open onClose={() => setPreview(null)} title={preview.name} width={900}>
          <PreviewBody source={source} entry={preview} url={previews[`${source.id}:${preview.path}`]} />
        </Modal>
      ) : null}
    </div>
  );
}

function Thumb({ entry, url }: { entry: SourceEntry; url?: string }) {
  const kind = entry.dir ? 'dir' : importableFile(entry.name)?.kind;
  const box = 'flex aspect-video w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-panel-2 text-faint';
  if (url && kind === 'image') return <img src={url} alt="" loading="lazy" className={cx(box, 'object-cover')} />;
  if (url && kind === 'video') return <video src={`${url}#t=1`} preload="metadata" muted playsInline className={cx(box, 'object-cover')} />;
  return (
    <span className={box}>
      {kind === 'dir' ? <Folder size={16} className="text-accent-2" /> : kind === 'video' ? <FileVideo size={16} /> : kind === 'audio' ? <FileAudio size={16} /> : kind === 'image' ? <FileImage size={16} /> : <FileIcon size={16} />}
    </span>
  );
}

function PreviewBody({ source, entry, url }: { source: SourceRow; entry: SourceEntry; url?: string }) {
  const kind = importableFile(entry.name)?.kind;
  return (
    <div className="grid gap-3">
      {source.kind !== 's3' ? (
        <div className="rounded-lg bg-panel-2 p-6 text-center text-sm text-muted">Previews are available for bucket connections. Import the file to watch it.</div>
      ) : !url ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : kind === 'image' ? (
        <img src={url} alt={entry.name} className="max-h-[70vh] w-full rounded-lg object-contain" />
      ) : kind === 'video' ? (
        <video src={url} controls autoPlay className="max-h-[70vh] w-full rounded-lg bg-black" />
      ) : kind === 'audio' ? (
        <audio src={url} controls autoPlay className="w-full" />
      ) : (
        <div className="rounded-lg bg-panel-2 p-6 text-center text-sm text-muted">No preview for this file.</div>
      )}
      <div className="flex flex-wrap gap-2 text-xs text-faint">
        <Badge>{entry.path}</Badge>
        <Badge>{formatBytes(entry.size)}</Badge>
        {entry.modifiedAt ? <Badge>{fmtDate(entry.modifiedAt)}</Badge> : null}
        <span>Browsers play H.264, VP9 and AV1; other codecs preview after import.</span>
      </div>
    </div>
  );
}

function TreeNode({
  source,
  path,
  label,
  depth,
  icon,
  folders,
  expanded,
  activeSource,
  activePath,
  onToggle,
  onOpen,
  load,
}: {
  source: SourceRow;
  path: string;
  label: string;
  depth: number;
  icon: React.ReactNode;
  folders: Map<string, Folder>;
  expanded: Set<string>;
  activeSource: string;
  activePath: string;
  onToggle: (key: string, open: boolean) => void;
  onOpen: (path: string) => void;
  load: (path: string) => void;
}) {
  const k = fkey(source.id, path);
  const open = expanded.has(k);
  const data = folders.get(k);
  const dirs = (data?.entries ?? []).filter((e) => e.dir).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  const active = activeSource === source.id && activePath === path;
  return (
    <div>
      <div
        className={cx('flex items-center gap-1 rounded-md py-1 pr-2 text-sm', active ? 'bg-accent/15 text-fg' : 'text-muted hover:bg-panel-3 hover:text-fg')}
        style={{ paddingLeft: 4 + depth * 14 }}
        data-tree-node={k}
      >
        <button
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-panel-3"
          aria-label={open ? 'Collapse' : 'Expand'}
          onClick={() => {
            if (!open) load(path);
            onToggle(k, !open);
          }}
        >
          {data?.loading && open ? <Spinner size={10} /> : open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <button className="flex min-w-0 flex-1 items-center gap-1.5 text-left" onClick={() => onOpen(path)} title={path || label}>
          <span className="shrink-0">{icon}</span>
          <span className="truncate">{label}</span>
        </button>
        {depth === 0 && source.lastError ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-danger" title={source.lastError} /> : null}
      </div>
      {open
        ? dirs.slice(0, 300).map((d) => (
            <TreeNode
              key={d.path}
              source={source}
              path={d.path}
              label={sourceBaseName(d.path)}
              depth={depth + 1}
              icon={<Folder size={14} className="text-accent-2" />}
              folders={folders}
              expanded={expanded}
              activeSource={activeSource}
              activePath={activePath}
              onToggle={onToggle}
              onOpen={onOpen}
              load={load}
            />
          ))
        : null}
    </div>
  );
}
